import React, { FC } from 'react'
import { ApexOptions } from 'apexcharts'
import format from 'date-fns/format'
import { Box, CircularProgress } from '@mui/material'
import { merge } from 'lodash'
import { Chart } from './chart'
import { tooltipRow, tooltipTitleBlock } from './tooltipHtml'

interface SensorChartProps {
  data: ApexOptions['series']
  options?: Partial<Omit<ApexOptions, 'series'>>
  isLoading?: boolean
  timeFrom?: Date
  timeTo?: Date
}

export const getCustomTooltip = ({ series, seriesIndex, dataPointIndex, w }: any): string => {
  const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex]
  const label = w.globals.initialSeries[seriesIndex].name
  const timestamp = w.globals.ttKeyFormatter(data.x)
  const readingValue = data.y
  const latitude: string = data.meta.latitude
    ? [
        '<div class="apexcharts-tooltip-goals-group">',
        '<span class="apexcharts-tooltip-text-goals-label">',
        'Latitude: ',
        '</span>',
        '<span class="apexcharts-tooltip-text-goals-value">',
        data.meta.latitude || '-',
        '</span>',
        '</div>'
      ].join('')
    : ''
  const longitude: string = data.meta.longitude
    ? [
        '<div class="apexcharts-tooltip-goals-group">',
        '<span class="apexcharts-tooltip-text-goals-label">',
        'Longitude: ',
        '</span>',
        '<span class="apexcharts-tooltip-text-goals-value">',
        data.meta.longitude || '-',
        '</span>',
        '</div>'
      ].join('')
    : ''
  const mainReading: string = !data.meta.isLocation
    ? [
        '<div class="apexcharts-tooltip-y-group">',
        '<span class="apexcharts-tooltip-text-y-label">',
        `${label}: `,
        '</span>',
        '<span class="apexcharts-tooltip-text-y-value">',
        readingValue,
        '</span>',
        '</div>'
      ].join('')
    : [latitude, longitude].join('')
  const contractor: string = [
    '<div class="apexcharts-tooltip-goals-group">',
    '<span class="apexcharts-tooltip-text-goals-label">',
    'Contractor: ',
    '</span>',
    '<span class="apexcharts-tooltip-text-goals-value">',
    data.meta.contractor || '-',
    '</span>',
    '</div>'
  ].join('')
  const project: string = [
    '<div class="apexcharts-tooltip-goals-group">',
    '<span class="apexcharts-tooltip-text-goals-label">',
    'Project: ',
    '</span>',
    '<span class="apexcharts-tooltip-text-goals-value">',
    data.meta.project || '-',
    '</span>',
    '</div>'
  ].join('')
  const section: string = [
    '<div class="apexcharts-tooltip-goals-group">',
    '<span class="apexcharts-tooltip-text-goals-label">',
    'Section: ',
    '</span>',
    '<span class="apexcharts-tooltip-text-goals-value">',
    data.meta.projectSection || '-',
    '</span>',
    '</div>'
  ].join('')
  const gapInMinutes: string = data.meta.gapInMinutes
    ? [
        '<div class="apexcharts-tooltip-goals-group">',
        '<span class="apexcharts-tooltip-text-goals-label">',
        'Gap in minutes: ',
        '</span>',
        '<span class="apexcharts-tooltip-text-goals-value">',
        data.meta.gapInMinutes,
        '</span>',
        '</div>'
      ].join('')
    : ''
  const rapidRhProbes: string =
    data.meta.rapidRhProbes?.length > 0
      ? [
          '<div class="apexcharts-tooltip-goals-group">',
          '<span class="apexcharts-tooltip-text-goals-label">',
          'Rapid RH probes: ',
          '</span>',
          '<span class="apexcharts-tooltip-text-goals-value">',
          data.meta.rapidRhProbes.join(','),
          '</span>',
          '</div>'
        ].join('')
      : ''
  const backgroundColor: string = [`<span class="apexcharts-tooltip-marker" style="background-color: ${data.fillColor};">`, '</span>'].join('')
  const timestampBlock: string = tooltipTitleBlock(timestamp)

  return (
    timestampBlock +
    '<div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">' +
    backgroundColor +
    '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">' +
    mainReading +
    contractor +
    project +
    section +
    gapInMinutes +
    rapidRhProbes +
    '</div>' +
    '</div>' +
    '</div>'
  )
}

export const getOrientationTooltip = ({ series, seriesIndex, dataPointIndex, w }: any): string => {
  const xSeries = w.globals.initialSeries[0]
  const ySeries = w.globals.initialSeries[1]
  const tiltSeries = w.globals.initialSeries[2]
  const xData = xSeries.data[dataPointIndex]
  const yData = ySeries.data[dataPointIndex]
  const tiltData = tiltSeries.data[dataPointIndex]
  const timestamp = w.globals.ttKeyFormatter(xData.x)
  const meta = xData.meta

  const coloredRow = (label: string, value: string, color: string): string =>
    [
      '<div class="apexcharts-tooltip-goals-group" style="display:flex;align-items:center;">',
      `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:6px;margin-bottom:7px;flex-shrink:0;"></span>`,
      '<span class="apexcharts-tooltip-text-goals-label">',
      `${label}: `,
      '</span>',
      '<span class="apexcharts-tooltip-text-goals-value">',
      value,
      '</span>',
      '</div>'
    ].join('')

  const timestampBlock = tooltipTitleBlock(timestamp)

  return (
    timestampBlock +
    '<div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">' +
    '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">' +
    coloredRow('X', xData.y, xData.fillColor) +
    coloredRow('Y', yData.y, yData.fillColor) +
    coloredRow('Tilt', tiltData.y, tiltData.fillColor) +
    tooltipRow('Contractor', meta?.contractor || '-') +
    tooltipRow('Project', meta?.project || '-') +
    tooltipRow('Section', meta?.projectSection || '-') +
    '</div>' +
    '</div>' +
    '</div>'
  )
}

export const SensorChart: FC<SensorChartProps> = ({ data, options = {}, isLoading = false, timeFrom, timeTo }) => {
  const now = new Date()
  const nowSnapped = new Date(now)
  nowSnapped.setMinutes(59, 59, 0)
  const nowAnnotations: ApexOptions['annotations'] =
    timeFrom && timeTo && now >= timeFrom && now <= timeTo
      ? {
          xaxis: [
            {
              x: nowSnapped.getTime(),
              borderColor: '#757575',
              strokeDashArray: 4,
              label: {
                text: 'Now',
                borderColor: '#757575',
                orientation: 'horizontal',
                position: 'top',
                style: {
                  color: '#fff',
                  background: '#757575',
                },
              },
            },
          ],
        }
      : {}

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: {
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        },
        export: {
          csv: {
            headerCategory: 'Time'
          }
        }
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 4
      }
    },
    dataLabels: {
      enabled: false
    },
    xaxis: {
      type: 'datetime',
      labels: {
        offsetX: 0,
        formatter: (timestamp) => {
          return format(Number(timestamp), 'MMM dd - h a')
        }
      }
    },
    tooltip: {
      x: {
        formatter: (timestamp) => {
          return format(timestamp, 'MMM dd - h a')
        }
      }
    }
  }

  const resultOptions = merge(chartOptions, { annotations: nowAnnotations }, options)
  return (
    <Box height="400px" ml={-2} width="calc(100% + 16px)" position="relative">
      {isLoading && (
        <>
          <Box
            position="absolute"
            width="100%"
            height="100%"
            zIndex={1}
            sx={{
              background: 'grey',
              opacity: 0.1
            }}
          />
          <Box position="absolute" width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
            <CircularProgress color="primary" size={26} />
          </Box>
        </>
      )}
      <Chart options={resultOptions} data={data} />
    </Box>
  )
}
